import { Info, Trash2 } from "lucide-react";
import { useState } from "react";
import { Process } from "../../../../api/recipeSimulator";
import { InputNumber } from "../../../../components/InputText/InputNumber";
import { Button } from "../../../../components/ui/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "../../../../components/ui/common/dialog";
import { RecipeIngredientType } from "../../../../config/enums/IngredientType.enum";
import { cn } from "../../../../lib/utils";
import { formatNumberFixed } from "../../../../utils/utils";
import { formatGenericIngredientDescription } from "../../recipeAddIngredient/utils/genericIngredientDescription.utils";
import { RecipeVariantColumn, RecipeVariantTableProps } from "./recipeVariantTable.types";

type DescriptionIngredient = {
  ingredientName: string;
  description?: string | null;
  status?: string | null;
  record?: string | null;
  allocationMethod?: string | null;
  databaseName?: string | null;
};

type Props = Omit<
  RecipeVariantTableProps,
  "fixedColumns" | "dynamicColumns"
> & {
  fixedColumns: RecipeVariantTableProps["fixedColumns"];
  dynamicColumns: RecipeVariantTableProps["dynamicColumns"];
};

const variationCellStyle = { backgroundColor: "hsl(var(--variation-bg))" };

const RecipeVariantTableBody = ({
  recipe,
  data,
  control,
  errors,
  totals,
  dynamicColumns,
  variants,
  addDeleteColumn = false,
  setProcessingStepVisibleModal,
  setSelectedProcess,
  setProcessingStepViewModal,
  setIsOriginal,
  setDeleteIngredient,
  setVariantId,
  onRemoveVariant,
  updateIngredientPercentage,
  recipeId,
  readOnly = false
}: Props) => {
  const [descriptionIngredient, setDescriptionIngredient] =
    useState<DescriptionIngredient | null>(null);

  const handlePercentageChange = (
    ingredientId: string,
    type: string,
    e: React.ChangeEvent<HTMLInputElement>,
    variantId?: string
  ) => {
    if (readOnly || !recipeId || !ingredientId || !type) return;

    const value = parseFloat(e.target.value.replace(/,/g, ""));
    if (isNaN(value)) return;

    const payload: any = {
      recipeId,
      ingredientId,
      type,
      percentage: value
    };
    if (variantId) {
      payload.variantId = variantId;
    }

    updateIngredientPercentage(payload);
  };

  const renderProcessList = (
    processes: Process[] | undefined,
    options: {
      isOriginal: boolean;
      variantId?: string | null;
    }
  ) => (
    <div className="process-list">
      {processes?.length ? (
        processes.map((proc, procIdx) => (
          <div key={`proc-${procIdx}`} className="salesUnitLink tw-flex tw-items-center tw-gap-1">
            <button
              type="button"
              className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-inherit tw-underline"
              onClick={() => {
                setSelectedProcess(proc);
                setProcessingStepViewModal(true);
                setIsOriginal(options.isOriginal);
                if (!options.isOriginal) {
                  setVariantId(options.variantId ?? null);
                }
              }}
            >
              {proc.processName}
            </button>
          </div>
        ))
      ) : (
        <p className="process-empty">-</p>
      )}
      {!readOnly && (
        <button
          type="button"
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-primary tw-underline hover:tw-text-primary/80"
          onClick={() => {
            setProcessingStepVisibleModal(true);
            setVariantId(options.isOriginal ? null : options.variantId ?? null);
            setIsOriginal(options.isOriginal);
          }}
        >
          + Add Process
        </button>
      )}
    </div>
  );

  return (
    <>
    <tbody>
      {recipe.originalRecipe.ingredients.map((ing: any, rowIdx: number) => (
        <tr
          key={`row-${rowIdx}`}
          className="tw-border-b tw-border-border hover:tw-bg-muted/30"
        >
          <td className="tw-px-4 tw-py-2.5 tw-text-foreground">
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <span className="tw-font-medium">{ing.ingredientName}</span>
              {ing.type === RecipeIngredientType.GENERIC_INGREDIENT ? (
                <button
                  type="button"
                  className="tw-inline-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary hover:tw-text-primary/80 tw-transition-colors"
                  aria-label={`View dataset description for ${ing.ingredientName}`}
                  onClick={() =>
                    setDescriptionIngredient({
                      ingredientName: ing.ingredientName,
                      description: ing.description,
                      status: ing.status,
                      record: ing.record,
                      allocationMethod: ing.allocationMethod,
                      databaseName: ing.databaseName
                    })
                  }
                >
                  <Info className="tw-h-4 tw-w-4" />
                </button>
              ) : null}
            </div>
          </td>
          <td className="tw-px-4 tw-py-2">
            <InputNumber
              control={control}
              errors={errors[`original_${ing.ingredientId}`]}
              name={`original_${ing.ingredientId}`}
              onChangeFunction={(e: React.ChangeEvent<HTMLInputElement>) =>
                handlePercentageChange(ing.ingredientId, ing.type, e)
              }
              enableLimitedDecimal
              viewDecimalNumber={5}
              customGridColumn="custom-padding-original-recipe-input"
              disabled={readOnly}
            />
          </td>
          {dynamicColumns.map((col: RecipeVariantColumn, colIdx: number) => {
            const actualVariantIdx = variants.findIndex(
              (v: { _id?: string }) => v._id === col.variantId
            );

            return (
              <td
                key={`cell-${rowIdx}-${colIdx}`}
                className="tw-px-4 tw-py-2"
                style={variationCellStyle}
              >
                <InputNumber
                  control={control}
                  errors={errors[`variant_${ing.ingredientId}_${actualVariantIdx}`]}
                  name={`variant_${ing.ingredientId}_${actualVariantIdx}`}
                  onChangeFunction={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handlePercentageChange(ing.ingredientId, ing.type, e, col.variantId)
                  }
                  enableLimitedDecimal
                  viewDecimalNumber={5}
                  customGridColumn="custom-padding-original-recipe-input"
                  disabled={readOnly}
                />
              </td>
            );
          })}
          {addDeleteColumn && !readOnly && dynamicColumns.length > 0 && (
            <td className="tw-px-2 tw-py-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="tw-h-7 tw-w-7 tw-text-destructive hover:tw-text-destructive hover:tw-bg-destructive/10"
                onClick={() => setDeleteIngredient(ing)}
              >
                <Trash2 className="tw-h-3.5 tw-w-3.5" />
              </Button>
            </td>
          )}
        </tr>
      ))}

      {recipe.originalRecipe.ingredients.length > 0 && (
        <tr
          className="tw-border-b tw-border-border tw-font-semibold"
          style={variationCellStyle}
        >
          <td className="tw-px-4 tw-py-2.5 tw-text-primary">Total</td>
          <td
            className={cn(
              "tw-px-4 tw-py-2.5 tw-text-center tw-text-primary",
              totals.original !== 100 && "recipe-total-wrong"
            )}
          >
            {formatNumberFixed(totals.original)}
          </td>
          {totals.variants.map((total: number, idx: number) => (
            <td
              key={`total-variant-${idx}`}
              className={cn(
                "tw-px-4 tw-py-2.5 tw-text-center tw-text-primary",
                total !== 100 && "recipe-total-wrong"
              )}
            >
              {formatNumberFixed(total)}
            </td>
          ))}
          {addDeleteColumn && dynamicColumns.length > 0 && <td />}
        </tr>
      )}

      <tr className="tw-border-b tw-border-border">
        <td className="tw-px-4 tw-py-3 tw-font-medium tw-text-foreground">Processing</td>
        <td className="tw-px-4 tw-py-3">
          {renderProcessList(data?.originalRecipe?.processes, { isOriginal: true })}
        </td>
        {dynamicColumns.map((col: RecipeVariantColumn, colIdx: number) => {
          const actualVariantIdx = variants.findIndex(
            (v: { _id?: string }) => v._id === col.variantId
          );
          const variantProcesses =
            actualVariantIdx !== -1 ? data?.variants?.[actualVariantIdx]?.processes ?? [] : [];

          return (
            <td
              key={`process-cell-${colIdx}`}
              className="tw-px-4 tw-py-3"
              style={variationCellStyle}
            >
              {renderProcessList(variantProcesses, {
                isOriginal: false,
                variantId: col.variantId
              })}
            </td>
          );
        })}
        {addDeleteColumn && dynamicColumns.length > 0 && <td />}
      </tr>

      {!readOnly && dynamicColumns.length > 0 && (
        <tr>
          <td />
          <td />
          {dynamicColumns.map((col: RecipeVariantColumn) => (
            <td key={`remove-${col.variantId}`} className="tw-px-4 tw-py-2 tw-text-center">
              {col.variantId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="tw-text-xs tw-text-destructive hover:tw-text-destructive hover:tw-bg-destructive/10 tw-gap-1"
                  onClick={() => onRemoveVariant?.(col.variantId!)}
                >
                  <Trash2 className="tw-h-3 tw-w-3" />
                  Remove
                </Button>
              )}
            </td>
          ))}
          {addDeleteColumn && <td />}
        </tr>
      )}
    </tbody>
    <Dialog
      open={!!descriptionIngredient}
      onOpenChange={open => !open && setDescriptionIngredient(null)}
    >
      <DialogContent className="tw-max-w-2xl tw-max-h-[85vh] tw-flex tw-flex-col">
        <DialogHeader>
          <DialogTitle>
            Dataset Description: {descriptionIngredient?.ingredientName}
          </DialogTitle>
          <DialogDescription className="tw-text-xs tw-text-gray-900">
            Read-only dataset information for this generic ingredient.
          </DialogDescription>
        </DialogHeader>
        <div className="tw-mt-2 tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-4 tw-py-3 tw-text-[12px] tw-leading-relaxed tw-text-black tw-whitespace-pre-wrap tw-overflow-y-auto tw-max-h-[50vh] tw-select-text">
          {descriptionIngredient
            ? formatGenericIngredientDescription(
                descriptionIngredient.description,
                descriptionIngredient.status,
                descriptionIngredient.record,
                descriptionIngredient.allocationMethod,
                descriptionIngredient.databaseName
              )
            : null}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default RecipeVariantTableBody;
